import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Spinner, ProgressBar } from 'react-bootstrap';
import { HardDriveDownload, Check } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useUsersData } from '../../Helpers/UsersContext';
import { useBuildingExport } from '../../hooks/superadmin/useBuildingExport';
import { isDirectSaveSupported, ExportProgress } from '../../api/buildingTransferApi';
import { formatFileSize } from '../../Helpers/FileUtils';
import SuperadminBreadcrumb from './SuperadminBreadcrumb';

const progressLabel = (progress: ExportProgress): string => {
    switch (progress.phase) {
        case 'preparing':
            return 'Läser byggnadsdata...';
        case 'zipping':
            return `Packar filer (${progress.filesDone} av ${progress.filesTotal}, ${formatFileSize(progress.bytesDone)})...`;
        case 'finishing':
            return 'Slutför arkivet...';
        default:
            return '';
    }
};

/**
 * Superadmin page: export one building — inventory, conditions, photos,
 * documents — to a ZIP file that can be imported on another instance's
 * byggnadsimport page. Primary use: copying prod data to test/demo.
 */
const BuildingExportPage: React.FC = () => {
    useDocumentTitle('Exportera byggnad');
    const navigate = useNavigate();
    const { users } = useUsersData();

    // Belt-and-braces guard in addition to the route guard
    const isSuperadmin = useMemo(() => {
        const uid = getAuth().currentUser?.uid;
        if (!uid || !users) return null; // null = still loading
        return users.find(u => u.key === uid)?.superadmin || false;
    }, [users]);

    useEffect(() => {
        if (isSuperadmin === false) {
            navigate('/superadmin');
        }
    }, [isSuperadmin, navigate]);

    const {
        loadingBuildings,
        buildings,
        selectedKey,
        setSelectedKey,
        exporting,
        progress,
        result,
        error,
        exportSelected,
    } = useBuildingExport();

    const saveSupported = isDirectSaveSupported();

    return (
        <>
        <SuperadminBreadcrumb title="Exportera byggnad" />
        <Container className="py-4" style={{ maxWidth: 700 }}>
            <h1 className="page-title mb-4">Exportera byggnad</h1>

            <Card>
                <Card.Header className="d-flex align-items-center gap-2">
                    <HardDriveDownload size={18} />
                    Byggnadsexport
                </Card.Header>
                <Card.Body className="p-4">
                    <p className="text-muted mb-3">
                        Exporterar en byggnad med komponenter, skick, foton och dokument till en
                        ZIP-fil som kan importeras i en annan instans. Historik, projekt,
                        ärenden och besiktningar ingår inte. Filen sparas direkt till disk
                        medan exporten pågår.
                    </p>

                    {!saveSupported && (
                        <Alert variant="warning">
                            Den här webbläsaren saknar stöd för att spara stora filer direkt till
                            disk. Använd Chrome eller Edge för att exportera.
                        </Alert>
                    )}

                    {error && <Alert variant="danger">{error}</Alert>}

                    {result && (
                        <Alert variant="success">
                            <div className="d-flex align-items-center gap-2">
                                <Check size={18} />
                                <strong>{result.manifest.buildingName}</strong> exporterades:{' '}
                                {result.manifest.counts.components} komponenter,{' '}
                                {result.manifest.counts.binaries} filer (
                                {formatFileSize(result.manifest.counts.totalBytes)})
                            </div>
                            {result.warnings.length > 0 && (
                                <ul className="mb-0 mt-2 ps-3">
                                    {result.warnings.map((warning, i) => <li key={i}>{warning}</li>)}
                                </ul>
                            )}
                        </Alert>
                    )}

                    {loadingBuildings ? (
                        <div className="text-center py-3">
                            <Spinner animation="border" />
                        </div>
                    ) : (
                        <Form.Group className="mb-4" controlId="export-building-select">
                            <Form.Label>Byggnad</Form.Label>
                            <Form.Select
                                value={selectedKey}
                                disabled={exporting}
                                onChange={event => setSelectedKey(event.target.value)}
                            >
                                <option value="">Välj byggnad...</option>
                                {buildings.map(building => (
                                    <option key={building.key} value={building.key}>
                                        {building.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}

                    {exporting && progress && (
                        <div className="mb-4">
                            <div className="text-muted mb-1">{progressLabel(progress)}</div>
                            <ProgressBar
                                animated={progress.phase !== 'zipping' || progress.filesTotal === 0}
                                now={
                                    progress.phase === 'zipping' && progress.filesTotal > 0
                                        ? (100 * progress.filesDone) / progress.filesTotal
                                        : 100
                                }
                            />
                        </div>
                    )}

                    <Button
                        variant="primary"
                        disabled={!saveSupported || loadingBuildings || exporting || !selectedKey}
                        onClick={exportSelected}
                    >
                        {exporting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Exporterar...
                            </>
                        ) : (
                            <>
                                <HardDriveDownload size={16} className="me-1" />
                                Exportera byggnad
                            </>
                        )}
                    </Button>
                </Card.Body>
            </Card>
        </Container>
        </>
    );
};

export default BuildingExportPage;
